import React, { useCallback, useEffect, useState } from 'react';
import { ErrorInfo, isErrorInfo } from '../../../shared/interfaces/error-info.interface';
import { TeamMemberUiModel, TeamUiModel } from '../../../shared/ui-models/team.ui-model';
import {
  archiveTeamMember,
  createTeamMember,
  getTeamMemberTree,
  getTeamMembers,
  getTeams,
  updateTeamMember
} from '../../../shared/services/data/team.data';
import Button, { ButtonType } from '../../../shared/components/core-ui/button/button.component';
import { Loader } from '../../../shared/components/core-ui/loader/loader.component';
import { birdToast } from '../../../shared/components/core-ui/toast';
import { MemberFormModal, MemberFormValue } from './components/member-form-modal.component';

const ROLE_LABEL: Record<string, string> = {
  manager: 'Manager',
  lead: 'Team Lead',
  senior: 'Senior',
  agent: 'Agent'
};

interface MemberRowProps {
  member: TeamMemberUiModel;
  depth: number;
  onEdit: (member: TeamMemberUiModel) => void;
  onArchive: (member: TeamMemberUiModel) => void;
}

const MemberRow: React.FC<MemberRowProps> = ({ member, depth, onEdit, onArchive }) => (
  <>
    <div
      className="flex flex-wrap items-center gap-3 border-b border-gray-100 py-3"
      style={{ paddingLeft: `${depth * 24}px` }}
    >
      <span className="font-medium">{member.name}</span>
      <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-600">
        {ROLE_LABEL[member.role] ?? member.role}
      </span>
      {member.teamNames.map((team) => (
        <span key={team} className="rounded-full bg-blue-50 px-2 py-0.5 text-xs text-blue-700">
          {team}
        </span>
      ))}
      {member.isOrphan ? (
        // Shown, not hidden. Their manager is missing from the tree, and a row
        // quietly dropped is a person nobody notices has no oversight.
        <span
          className="rounded-full bg-amber-50 px-2 py-0.5 text-xs text-amber-700"
          title="Reports to someone who is not in this tree"
        >
          unattached
        </span>
      ) : null}
      <span className="ml-auto flex gap-2">
        <button type="button" className="text-sm text-blue-600" onClick={() => onEdit(member)}>
          Edit
        </button>
        <button type="button" className="text-sm text-red-600" onClick={() => onArchive(member)}>
          Archive
        </button>
      </span>
    </div>
    {member.children.map((child) => (
      <MemberRow
        key={child.userId}
        member={child}
        depth={depth + 1}
        onEdit={onEdit}
        onArchive={onArchive}
      />
    ))}
  </>
);

export const TeamStructureContainer: React.FC = () => {
  const [tree, setTree] = useState<TeamMemberUiModel[]>([]);
  const [flat, setFlat] = useState<TeamMemberUiModel[]>([]);
  const [teams, setTeams] = useState<TeamUiModel[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<TeamMemberUiModel | undefined>();

  const load = useCallback(() => {
    setLoading(true);
    return Promise.all([getTeamMemberTree(), getTeamMembers(), getTeams()]).then(
      ([treeResult, flatResult, teamsResult]) => {
        if (!isErrorInfo(treeResult)) setTree(treeResult);
        if (!isErrorInfo(flatResult)) setFlat(flatResult);
        if (!isErrorInfo(teamsResult)) setTeams(teamsResult);
        setLoading(false);
      }
    );
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleSubmit = (value: MemberFormValue) => {
    const isEdit = Boolean(editing);
    const request = isEdit
      ? updateTeamMember(value.userId, {
          role: value.role,
          team: value.team,
          reportsTo: value.reportsTo
        })
      : createTeamMember({
          userId: value.userId,
          role: value.role,
          team: value.team,
          reportsTo: value.reportsTo
        });

    const promise = request.then((response: unknown) => {
      if (isErrorInfo(response as ErrorInfo)) throw response;
      setShowForm(false);
      setEditing(undefined);
      return load();
    });

    birdToast.promise(promise, {
      loadingMessage: isEdit ? 'Saving member...' : 'Adding member...',
      successMessage: isEdit ? 'Member saved' : 'Member added',
      errorPermissionMessage: 'You do not have permission to change the team structure',
      errorFailedMessage: isEdit ? 'Failed to save member' : 'Failed to add member'
    });
  };

  const handleArchive = (member: TeamMemberUiModel) => {
    const promise = archiveTeamMember(member.userId).then((response: boolean | ErrorInfo) => {
      if (isErrorInfo(response)) throw response;
      return load();
    });

    birdToast.promise(promise, {
      loadingMessage: 'Archiving member...',
      successMessage: 'Member archived',
      errorPermissionMessage: 'You do not have permission to archive a member',
      // The service refuses while they still have direct reports, and says so.
      // Keeping the generic message would hide the one thing that fixes it.
      errorFailedMessage: 'Could not archive — move their direct reports first'
    });
  };

  return (
    <div className="flex h-full flex-col items-stretch md:min-h-[calc(100vh-45px)] md:bg-[#f5f7fc] md:px-[2rem] md:py-[3rem]">
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <h1 className="text-xl font-semibold">Team structure</h1>
        <span className="text-sm text-gray-500">{flat.length} members</span>
        <span className="ml-auto">
          <Button
            type={ButtonType.primary}
            onClick={() => {
              setEditing(undefined);
              setShowForm(true);
            }}
          >
            Add member
          </Button>
        </span>
      </div>

      <div className="rounded bg-white p-4 shadow-sm">
        {loading ? (
          <Loader />
        ) : tree.length === 0 ? (
          <p className="py-8 text-center text-gray-500">
            No members yet. Add a manager first — everyone else reports to someone.
          </p>
        ) : (
          tree.map((root) => (
            <MemberRow
              key={root.userId}
              member={root}
              depth={0}
              onEdit={(member) => {
                setEditing(member);
                setShowForm(true);
              }}
              onArchive={handleArchive}
            />
          ))
        )}
      </div>

      <MemberFormModal
        show={showForm}
        member={editing}
        teams={teams}
        members={flat}
        onClose={() => {
          setShowForm(false);
          setEditing(undefined);
        }}
        onSubmit={handleSubmit}
      />
    </div>
  );
};
